import { Injectable } from '@angular/core';
import swal, { SweetAlertOptions } from 'sweetalert2';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { TranslateService } from './translate.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  constructor(private router: Router, private translateService: TranslateService) { }

  getSwalOptions(
    icon: any,
    html: string,
    title: string,
    showConfirmButton: boolean,
    confirmButtonText: string,
    allowOutsideClick: boolean = false) {
    const options: SweetAlertOptions = {
      title: title,
      html: html,
      icon: icon,
      showConfirmButton: showConfirmButton,
      confirmButtonText: confirmButtonText,
      confirmButtonColor: '#00a657',
      allowOutsideClick: allowOutsideClick
    };
    return options;
  }

  showInternalErrorMessage() {
    swal.fire(this.getSwalOptions('error', 'Finishline has encountered an error please contact the Help Desk.',
      'Error', true, 'Ok'))
      .then(result => {
        if (result && result.value) {
        }
      });
  }

  canEditTransaction() {
    swal.fire(this.getSwalOptions('Warning',
      'Only Approved Users can view/edit the transaction. Please contact the Help Desk for access.',
      'warning', true, 'Ok'))
      .then(result => {
        if (result && result.value) {
          this.router.navigate([`/landing`]);
        }
      });
  }

  customErrorMessage(errorMessage: string) {
    return swal.fire(
      this.getSwalOptions('error', errorMessage, 'Error', true, 'Ok')
    );
  }

  calculationMetricErrorMessage(): any {
    return swal.fire(
      this.getSwalOptions('error', this.translateService.getTranslation('CALCULATION_METRIC_ERROR'), 'Error', true, 'Ok')
    );
  }

  handleError<T>(error: any, fallbackData?: T): Observable<T> {
    const errorMessage = this.getErrorMessage(error);
    this.showApiErrorMessage(errorMessage, error?.status);
    return of(fallbackData as T);
  }

  private getErrorMessage(error: any): string {
    const statusCode = error?.status || error?.error?.status;
    switch (statusCode) {
      case 0:
        return 'Network error: Unable to connect to server';
      case 400:
        return 'Bad request: Please check your input and try again';
      case 401:
        return 'Authentication required. Please log in again';
      case 403:
        return 'You do not have permission to access this resource';
      case 404:
        return 'Requested resource not found';
      case 408:
        return 'Request timeout: Please try again';
      case 409:
        return 'Conflict: The request could not be completed due to a conflict';
      case 422:
        return 'Validation error: Please check your input';
      case 429:
        return 'Too many requests: Please wait a moment and try again';
      case 500:
        return 'Internal server error: Please try again later';
      case 502:
        return 'Bad gateway: Server is temporarily unavailable';
      case 503:
        return 'Service unavailable: Please try again later';
      case 504:
        return 'Gateway timeout: Please try again later';
      default:
        if (statusCode >= 500) {
          return 'Server error: Please try again later';
        } else if (statusCode >= 400) {
          return 'Client error: Please check your request and try again';
        }
        return 'An unexpected error occurred';
    }
  }

  showApiErrorMessage(message: string, statusCode?: number): void {
    /* Avoid popup for authentication/authorization error codes.
    It is handled in Api Service -> handleError() by redirecting to login page and deleting token.
    401: Unauthorized, 403: Forbidden, 408: Timeout (could be token expiry)
    */
    if (['401', '403', '408'].includes(`${statusCode}`)) {
      return;
    }
    const title = statusCode ? `Error ${statusCode}` : 'Error';
    swal.fire(this.getSwalOptions('error', message, title, true, 'Ok'));
  }
}
